import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import {
  createVariation,
  CreateVariationPayload,
  CreateVariationResponse,
} from '@/api/v2/variations/createVariation';
import {
  getVariations,
  GetVariationsResponse,
} from '@/api/v2/variations/getVariations';
import {
  updateVariation,
  UpdateVariationPayload,
  UpdateVariationResponse,
} from '@/api/v2/variations/updateVariation';

// Define the argument type for creating variation
export interface CreateVariationArgs {
  jobUuid: string;
  variationData: CreateVariationPayload;
}

// Define error type for variation operations
export interface VariationError {
  message: string;
  messages?: Record<string, string[]>;
}

// Thunk for creating a variation
export const createVariationThunk = createAsyncThunk<
  CreateVariationResponse, // Success response type
  CreateVariationArgs, // Request payload type
  { rejectValue: VariationError } // Error type for rejection
>(
  'variations/createVariation',
  async ({ jobUuid, variationData }, { rejectWithValue }) => {
    try {
      // Call the API function with jobUuid and variation data
      const response = await createVariation(jobUuid, variationData);

      // Show success message
      toast.success('Variation created successfully!');

      return response; // Return response data
    } catch (error: any) {
      // Handle different types of errors
      if (error.response) {
        const { message, messages } = error.response.data;
        toast.error(message || 'Failed to create variation');
        return rejectWithValue({ message, messages });
      }

      // Handle network or other errors
      const errorMessage =
        error.message || 'An unknown error occurred while creating variation';
      toast.error(errorMessage);
      return rejectWithValue({ message: errorMessage });
    }
  },
);

// Define the argument type for fetching variations
export interface FetchVariationsArgs {
  jobUuid: string;
}

// Define the argument type for updating variation
export interface UpdateVariationArgs {
  jobUuid: string;
  variationUuid: string;
  variationData: UpdateVariationPayload;
}

// Thunk for fetching variations
export const fetchVariationsThunk = createAsyncThunk<
  GetVariationsResponse, // Success response type
  FetchVariationsArgs, // Request payload type
  { rejectValue: VariationError } // Error type for rejection
>('variations/fetchVariations', async ({ jobUuid }, { rejectWithValue }) => {
  try {
    // Call the API function with jobUuid
    const response = await getVariations(jobUuid);

    return response; // Return response data
  } catch (error: any) {
    // Handle different types of errors
    if (error.response) {
      const { message, messages } = error.response.data;
      // Don't show toast for fetch errors as they might be expected
      return rejectWithValue({ message, messages });
    }

    // Handle network or other errors
    const errorMessage =
      error.message || 'An unknown error occurred while fetching variations';
    return rejectWithValue({ message: errorMessage });
  }
});

// Thunk for updating a variation
export const updateVariationThunk = createAsyncThunk<
  UpdateVariationResponse, // Success response type
  UpdateVariationArgs, // Request payload type
  { rejectValue: VariationError } // Error type for rejection
>(
  'variations/updateVariation',
  async ({ jobUuid, variationUuid, variationData }, { rejectWithValue }) => {
    try {
      // Call the API function with jobUuid, variationUuid and variation data
      const response = await updateVariation(
        jobUuid,
        variationUuid,
        variationData,
      );

      // Show success message
      toast.success('Variation updated successfully!');

      return response; // Return response data
    } catch (error: any) {
      // Handle different types of errors
      if (error.response) {
        const { message, messages } = error.response.data;
        toast.error(message || 'Failed to update variation');
        return rejectWithValue({ message, messages });
      }

      // Handle network or other errors
      const errorMessage =
        error.message || 'An unknown error occurred while updating variation';
      toast.error(errorMessage);
      return rejectWithValue({ message: errorMessage });
    }
  },
);
